import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { ApiError } from '@/services/api';
import {
  esportesPortalApi,
  type PortalConfig,
  type PortalConfigUpdate,
  type PortalStatus,
} from '@/services/esportesPortal';

import styles from './ConfigPortalEsportes.module.css';

/**
 * PORTAL DE ESPORTES — liga, configura, roda, mostra o que publicou e desliga.
 *
 * Tudo que o ciclo automático lê sai desta tela (nada pelo .env). O interruptor
 * geral desliga pelo endpoint próprio (`/desligar`), que audita a ação como
 * desligamento e não como uma edição qualquer.
 */

const INTERRUPTORES: { campo: keyof PortalConfigUpdate; rotulo: string; ajuda: string }[] = [
  { campo: 'publicar_tabelas', rotulo: 'Tabelas', ajuda: 'Página de classificação de cada campeonato.' },
  { campo: 'publicar_clubes', rotulo: 'Clubes', ajuda: 'Uma página por clube, com elenco e notícias.' },
  { campo: 'publicar_atletas', rotulo: 'Atletas', ajuda: 'Uma página por atleta do elenco.' },
  { campo: 'publicar_pre_jogo', rotulo: 'Serviço de jogo', ajuda: 'Post de pré-jogo: data, local, onde assistir, escalação.' },
  { campo: 'publicar_pos_jogo', rotulo: 'Pós-jogo', ajuda: 'Post com placar e resumo depois da partida.' },
  { campo: 'marcar_noticias', rotulo: 'Marcar notícias', ajuda: 'Liga notícias do site às páginas de clube e atleta.' },
];

const INTERVALOS = [15, 30, 60, 120, 240, 720, 1440];

const TIPOS: Record<string, string> = {
  hub: 'Página inicial',
  tabela: 'Tabela',
  clube: 'Clube',
  atleta: 'Atleta',
  pre_jogo: 'Pré-jogo',
  pos_jogo: 'Pós-jogo',
  rodada: 'Rodada',
  noticia: 'Notícia',
};

function rotuloIntervalo(min: number): string {
  if (min < 60) return `a cada ${min} min`;
  if (min === 1440) return 'uma vez por dia';
  const h = min / 60;
  return `a cada ${h} hora${h > 1 ? 's' : ''}`;
}

function quando(iso: string | null): string {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  const seg = Math.round((Date.now() - d.getTime()) / 1000);
  const abs = d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  if (seg < 60) return `agora há pouco (${abs})`;
  if (seg < 3600) return `há ${Math.round(seg / 60)} min (${abs})`;
  if (seg < 86400) return `há ${Math.round(seg / 3600)} h (${abs})`;
  return abs;
}

function mensagemDeErro(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: unknown })?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d[0]?.msg) return String(d[0].msg);
    return `Falha ${e.status}`;
  }
  return 'Não foi possível falar com o servidor.';
}

function rascunhoDe(c: PortalConfig): PortalConfigUpdate {
  return {
    competicoes: [...c.competicoes],
    temporada: c.temporada,
    publicar_tabelas: c.publicar_tabelas,
    publicar_clubes: c.publicar_clubes,
    publicar_atletas: c.publicar_atletas,
    publicar_pre_jogo: c.publicar_pre_jogo,
    publicar_pos_jogo: c.publicar_pos_jogo,
    marcar_noticias: c.marcar_noticias,
    antecedencia_dias: c.antecedencia_dias,
    intervalo_min: c.intervalo_min,
    autor_wp_id: c.autor_wp_id,
  };
}

export function ConfigPortalEsportes() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  const sitesQ = useQuery({ queryKey: ['esportes-portal', 'sites'], queryFn: esportesPortalApi.sites });
  const sites = useMemo(() => sitesQ.data ?? [], [sitesQ.data]);
  const slug = params.get('site') ?? sites[0]?.site_slug ?? null;
  const cfg = sites.find((s) => s.site_slug === slug) ?? null;

  // O rascunho só volta ao gravado quando o GRAVADO muda (troca de site ou salvar), e não a
  // cada refetch da lista: foco na janela não pode apagar o que o operador está editando.
  const gravado = cfg ? JSON.stringify(rascunhoDe(cfg)) : null;
  const [rascunho, setRascunho] = useState<PortalConfigUpdate | null>(null);
  useEffect(() => {
    if (gravado) setRascunho(JSON.parse(gravado) as PortalConfigUpdate);
  }, [gravado, slug]);

  const autoresQ = useQuery({
    queryKey: ['esportes-portal', 'autores', slug],
    queryFn: () => esportesPortalApi.autores(slug as string),
    enabled: !!slug,
    staleTime: 5 * 60_000,
  });

  const statusQ = useQuery({
    queryKey: ['esportes-portal', 'status', slug],
    queryFn: () => esportesPortalApi.status(slug as string),
    enabled: !!slug,
    refetchInterval: (q) => ((q.state.data as PortalStatus | undefined)?.rodando ? 3000 : 30_000),
  });

  const atualizar = (novo: PortalConfig) => {
    qc.setQueryData<PortalConfig[]>(['esportes-portal', 'sites'], (lista) =>
      (lista ?? []).map((s) => (s.site_slug === novo.site_slug ? novo : s)),
    );
    qc.invalidateQueries({ queryKey: ['esportes-portal', 'status', novo.site_slug] });
  };

  const salvar = useMutation({
    mutationFn: (dados: PortalConfigUpdate) => esportesPortalApi.salvar(slug as string, dados),
    onSuccess: atualizar,
  });
  const ligar = useMutation({
    mutationFn: () => esportesPortalApi.salvar(slug as string, { ativo: true }),
    onSuccess: atualizar,
  });
  const desligar = useMutation({
    mutationFn: () => esportesPortalApi.desligar(slug as string),
    onSuccess: atualizar,
  });
  const rodar = useMutation({
    mutationFn: () => esportesPortalApi.rodar(slug as string),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['esportes-portal', 'status', slug] }),
  });

  if (sitesQ.isLoading) {
    return (
      <section className={styles.root}>
        <p className={styles.muted}>Carregando…</p>
      </section>
    );
  }
  if (sitesQ.isError) {
    return (
      <section className={styles.root}>
        <Cabecalho />
        <div className={styles.erroCaixa} role="alert">
          Não foi possível carregar os sites: {mensagemDeErro(sitesQ.error)}
          <Button variant="secondary" size="sm" onClick={() => sitesQ.refetch()}>
            Tentar de novo
          </Button>
        </div>
      </section>
    );
  }
  if (sites.length === 0) {
    return (
      <section className={styles.root}>
        <Cabecalho />
        <div className={styles.vazio}>
          <p>Nenhum site WordPress cadastrado. O portal publica num site do cadastro.</p>
          <Link to="/config/sites" className={styles.link}>
            Cadastrar um site
          </Link>
        </div>
      </section>
    );
  }
  if (!cfg || !rascunho) {
    return (
      <section className={styles.root}>
        <Cabecalho />
        <div className={styles.vazio}>
          <p>Site “{slug}” não encontrado no seu acesso.</p>
          <button className={styles.link} onClick={() => setParams({})}>
            Voltar ao primeiro site
          </button>
        </div>
      </section>
    );
  }

  const alterado = JSON.stringify(rascunho) !== JSON.stringify(rascunhoDe(cfg));
  const patch = (p: PortalConfigUpdate) => setRascunho({ ...rascunho, ...p });
  const status = statusQ.data;
  const rodando = !!status?.rodando || cfg.rodando;
  const autores = autoresQ.data?.autores ?? [];
  const autorAtual = rascunho.autor_wp_id ?? null;
  const trocandoInterruptor = ligar.isPending || desligar.isPending;
  const erroInterruptor = ligar.error ?? desligar.error;

  return (
    <section className={styles.root}>
      <Cabecalho />

      {sites.length > 1 && (
        <label className={styles.campoLinha}>
          <span className={styles.rotulo}>Site</span>
          <select
            className={styles.select}
            value={cfg.site_slug}
            onChange={(e) => setParams({ site: e.target.value })}
          >
            {sites.map((s) => (
              <option key={s.site_slug} value={s.site_slug}>
                {s.site_nome} ({s.site_url.replace(/^https?:\/\//, '')})
              </option>
            ))}
          </select>
        </label>
      )}

      {/* Interruptor geral */}
      <div className={`${styles.geral} ${cfg.ativo ? styles.geralLigado : ''}`}>
        <div>
          <p className={styles.geralEstado}>{cfg.ativo ? 'Portal ligado' : 'Portal desligado'}</p>
          <p className={styles.geralTexto}>
            {cfg.ativo
              ? `${cfg.site_nome} se publica sozinho, ${rotuloIntervalo(cfg.intervalo_min)}.`
              : `Nada é publicado em ${cfg.site_nome}. O que já saiu continua no site.`}
          </p>
          {!cfg.site_ativo && (
            <p className={styles.aviso}>
              Este site está inativo no cadastro de sites: o portal não roda até ele ser reativado.
            </p>
          )}
          {erroInterruptor && <p className={styles.erroTexto}>{mensagemDeErro(erroInterruptor)}</p>}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={cfg.ativo}
          aria-label="Interruptor geral do portal"
          className={`${styles.chave} ${cfg.ativo ? styles.chaveLigada : ''}`}
          disabled={trocandoInterruptor}
          onClick={() => (cfg.ativo ? desligar.mutate() : ligar.mutate())}
        >
          <span className={styles.chaveBolinha} />
        </button>
      </div>

      {/* Configuração */}
      <div className={styles.cartao}>
        <div className={styles.cartaoCabeca}>
          <h2 className={styles.cartaoTitulo}>O que publicar</h2>
          <Button onClick={() => salvar.mutate(rascunho)} disabled={!alterado || salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
        {salvar.isError && <p className={styles.erroTexto}>{mensagemDeErro(salvar.error)}</p>}
        {salvar.isSuccess && !alterado && <p className={styles.ok}>Configuração salva.</p>}

        <fieldset className={styles.grupo}>
          <legend className={styles.rotulo}>Campeonatos</legend>
          <div className={styles.opcoes}>
            {cfg.competicoes_disponiveis.map((c) => {
              const marcado = rascunho.competicoes?.includes(c.id) ?? false;
              return (
                <label key={c.id} className={styles.opcao}>
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() =>
                      patch({
                        competicoes: marcado
                          ? (rascunho.competicoes ?? []).filter((x) => x !== c.id)
                          : [...(rascunho.competicoes ?? []), c.id],
                      })
                    }
                  />
                  {c.nome}
                </label>
              );
            })}
          </div>
          {rascunho.competicoes?.length === 0 && (
            <p className={styles.aviso}>Escolha pelo menos um campeonato.</p>
          )}
        </fieldset>

        <fieldset className={styles.grupo}>
          <legend className={styles.rotulo}>Peças do portal</legend>
          <div className={styles.interruptores}>
            {INTERRUPTORES.map((i) => (
              <label key={i.campo} className={styles.interruptor}>
                <input
                  type="checkbox"
                  checked={!!rascunho[i.campo]}
                  onChange={(e) => patch({ [i.campo]: e.target.checked })}
                />
                <span>
                  <strong>{i.rotulo}</strong>
                  <small>{i.ajuda}</small>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className={styles.linhaCampos}>
          <label className={styles.campo}>
            <span className={styles.rotulo}>Serviço de jogo sai</span>
            <select
              className={styles.select}
              value={rascunho.antecedencia_dias}
              onChange={(e) => patch({ antecedencia_dias: Number(e.target.value) })}
            >
              {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                <option key={d} value={d}>
                  {d} dia{d > 1 ? 's' : ''} antes do jogo
                </option>
              ))}
            </select>
          </label>
          <label className={styles.campo}>
            <span className={styles.rotulo}>Ciclo automático</span>
            <select
              className={styles.select}
              value={rascunho.intervalo_min}
              onChange={(e) => patch({ intervalo_min: Number(e.target.value) })}
            >
              {[...new Set([...INTERVALOS, rascunho.intervalo_min ?? 30])]
                .sort((a, b) => a - b)
                .map((m) => (
                  <option key={m} value={m}>
                    {rotuloIntervalo(m)}
                  </option>
                ))}
            </select>
          </label>
          <label className={styles.campo}>
            <span className={styles.rotulo}>Temporada</span>
            <input
              className={styles.input}
              type="number"
              min={2000}
              max={2100}
              placeholder="atual"
              value={rascunho.temporada ?? ''}
              onChange={(e) => patch({ temporada: e.target.value ? Number(e.target.value) : null })}
            />
          </label>
          <label className={styles.campo}>
            <span className={styles.rotulo}>Autor no WordPress</span>
            {autores.length > 0 ? (
              <select
                className={styles.select}
                value={autorAtual ?? ''}
                onChange={(e) => patch({ autor_wp_id: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">
                  Padrão do site
                  {cfg.autor_padrao_site
                    ? ` (${autores.find((a) => a.id === cfg.autor_padrao_site)?.nome ?? `id ${cfg.autor_padrao_site}`})`
                    : ''}
                </option>
                {autores.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nome}
                  </option>
                ))}
                {autorAtual && !autores.some((a) => a.id === autorAtual) && (
                  <option value={autorAtual}>id {autorAtual} (não encontrado no site)</option>
                )}
              </select>
            ) : (
              <input
                className={styles.input}
                type="number"
                min={1}
                placeholder={autoresQ.isLoading ? 'buscando autores…' : 'id do autor (vazio = padrão)'}
                value={autorAtual ?? ''}
                onChange={(e) => patch({ autor_wp_id: e.target.value ? Number(e.target.value) : null })}
              />
            )}
            {autoresQ.data?.erro && <small className={styles.avisoPequeno}>{autoresQ.data.erro}</small>}
          </label>
        </div>
      </div>

      {/* Status */}
      <div className={styles.cartao}>
        <div className={styles.cartaoCabeca}>
          <div>
            <h2 className={styles.cartaoTitulo}>O que o portal fez</h2>
            <p className={styles.muted}>
              Última execução: {rodando ? 'rodando agora…' : quando(status?.ultima_execucao_em ?? cfg.ultima_execucao_em)}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => rodar.mutate()}
            disabled={!cfg.ativo || rodando || rodar.isPending || !cfg.site_ativo}
            title={cfg.ativo ? 'Roda um ciclo agora, sem esperar o intervalo' : 'Ligue o portal para rodar'}
          >
            {rodando ? 'Rodando…' : 'Rodar agora'}
          </Button>
        </div>
        {rodar.isError && <p className={styles.erroTexto}>{mensagemDeErro(rodar.error)}</p>}
        {rodar.data && !rodar.data.disparado && <p className={styles.aviso}>{rodar.data.mensagem}</p>}

        {statusQ.isError ? (
          <div className={styles.erroCaixa} role="alert">
            Não foi possível ler o status: {mensagemDeErro(statusQ.error)}
          </div>
        ) : !status ? (
          <p className={styles.muted}>Carregando…</p>
        ) : (
          <>
            <div className={styles.numeros}>
              <Numero valor={status.criados} rotulo="criados" />
              <Numero valor={status.atualizados} rotulo="atualizados" />
              <Numero valor={status.iguais} rotulo="sem mudança" />
              <Numero valor={status.erros.length} rotulo="erros" tom={status.erros.length ? 'crit' : undefined} />
              <Numero valor={status.total_publicado} rotulo="no site, ao todo" />
            </div>

            {Object.keys(status.por_tipo).length > 0 && (
              <p className={styles.porTipo}>
                {Object.entries(status.por_tipo)
                  .sort((a, b) => b[1] - a[1])
                  .map(([t, n]) => `${TIPOS[t] ?? t}: ${n}`)
                  .join(' · ')}
              </p>
            )}

            {(status.ultimo_erro || status.erros.length > 0) && (
              <div className={styles.erroCaixa} role="alert">
                <strong>O que deu errado no último ciclo</strong>
                <ul>
                  {status.ultimo_erro && <li>{status.ultimo_erro}</li>}
                  {status.erros.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}

            <h3 className={styles.subtitulo}>Últimas publicações</h3>
            {status.recentes.length === 0 ? (
              <p className={styles.muted}>
                {cfg.ativo
                  ? 'Nada publicado ainda. Use “Rodar agora” para o primeiro ciclo.'
                  : 'Nada publicado ainda. Ligue o portal para começar.'}
              </p>
            ) : (
              <table className={styles.tabela}>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Item</th>
                    <th>Atualizado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {status.recentes.map((p) => (
                    <tr key={p.chave}>
                      <td>{TIPOS[p.tipo] ?? p.tipo}</td>
                      <td className={styles.mono}>{p.chave}</td>
                      <td>{new Date(p.atualizado_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td>
                        {p.url ? (
                          <a href={p.url} target="_blank" rel="noreferrer" className={styles.link}>
                            abrir no site
                          </a>
                        ) : (
                          <span className={styles.muted}>sem link</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Cabecalho() {
  return (
    <header>
      <h1 className={styles.titulo}>Portal de esportes</h1>
      <p className={styles.subtituloPagina}>
        Site de esportes que se publica sozinho: tabelas, páginas de clube e atleta, serviço de
        jogo e pós-jogo. Escolha o site, ligue, ajuste o que sai e acompanhe aqui.
      </p>
    </header>
  );
}

function Numero({ valor, rotulo, tom }: { valor: number; rotulo: string; tom?: 'crit' }) {
  return (
    <div className={styles.numero}>
      <span className={`${styles.numeroValor} ${tom === 'crit' ? styles.crit : ''}`}>{valor}</span>
      <span className={styles.numeroRotulo}>{rotulo}</span>
    </div>
  );
}
